import { useCallback, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, submitWaitlistRequest } from "../lib/api.js";
import { isTurnstileConfigured } from "../lib/captcha.js";
import { TurnstileWidget } from "./TurnstileWidget.js";
import { AuthForm, ErrorBanner, SuccessBanner, fieldCls, labelCls } from "./AuthShell.js";
import { Button } from "./ui/Button.js";
import { InlineSpinner } from "./ui/InlineSpinner.js";
import { showErrorToast } from "./ui/Toast.js";

const NOTE_MAX = 500;
const SUCCESS =
  "Thanks - if a spot opens, we'll email you at that address.";

function waitlistErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429 || err.code === "RATE_LIMITED") {
      return "Too many requests from this network. Try again in an hour.";
    }
    if (err.status === 403 || err.code === "NOT_ACCEPTING") {
      return "Access requests aren't open right now.";
    }
    if (err.status === 503 || err.code === "TRY_LATER") {
      return "The bot check couldn't be completed. Try again in a moment.";
    }
    if (err.code === "BOT_CHECK_FAILED") {
      return "The bot check didn't pass. Please try again.";
    }
  }
  return "Could not send your request.";
}

export function WaitlistRequestForm({ initialEmail = "" }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const captchaRequired = isTurnstileConfigured();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [widgetNonce, setWidgetNonce] = useState(0);
  const websiteRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const handleVerify = useCallback((token: string | null) => {
    setCaptchaToken(token);
  }, []);

  useLayoutEffect(() => {
    const el = noteRef.current;
    if (!el) return;
    el.style.height = "auto";
    const line = 24;
    const min = line * 3;
    const max = line * 8;
    const next = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [note]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (captchaRequired && !captchaToken) {
      const message = "Please complete the bot check below.";
      setError(message);
      showErrorToast(message);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const trimmedNote = note.trim();
      await submitWaitlistRequest({
        email: email.trim(),
        ...(trimmedNote ? { note: trimmedNote } : {}),
        website: websiteRef.current?.value ?? "",
        ...(captchaToken ? { turnstileToken: captchaToken } : {}),
      });
      setSent(true);
    } catch (err) {
      setCaptchaToken(null);
      setWidgetNonce((n) => n + 1);
      const message = waitlistErrorMessage(err);
      setError(message);
      showErrorToast(message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return <SuccessBanner message={SUCCESS} />;
  }

  return (
    <AuthForm onSubmit={(e) => void handleSubmit(e)}>
      <ErrorBanner message={error} />

      <div>
        <label htmlFor="waitlist-email" className={labelCls}>
          Email
        </label>
        <input
          id="waitlist-email"
          type="email"
          autoComplete="email"
          required
          className={fieldCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="waitlist-note" className={labelCls}>
          Note <span className="font-normal text-muted-sm">(optional)</span>
        </label>
        <textarea
          id="waitlist-note"
          ref={noteRef}
          className={`${fieldCls} resize-none`}
          maxLength={NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
        />
        <p className="mt-1 text-right text-xs text-muted-sm">
          {note.length}/{NOTE_MAX}
        </p>
      </div>

      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <input
          ref={websiteRef}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
      </div>

      {captchaRequired && (
        <TurnstileWidget key={widgetNonce} onVerify={handleVerify} action="waitlist" />
      )}

      <Button
        type="submit"
        fullWidth
        className="min-w-40"
        disabled={busy || (captchaRequired && !captchaToken)}
        aria-busy={busy}
      >
        {busy ? (
          <>
            <InlineSpinner />
            Requesting…
          </>
        ) : (
          "Request access"
        )}
      </Button>
    </AuthForm>
  );
}
